<template>
  <v-app>
    <!-- Show header and nav only when authenticated -->
    <template v-if="authStore.isAuthenticated">
      <!-- Top Logo/Login Bar -->
      <v-app-bar
        app
        height="60"
        color="white"
        elevation="1"
      >
        <!-- Hamburger Menu -->
        <v-app-bar-nav-icon @click="toggleDrawer" class="app-bar-btn-primary"></v-app-bar-nav-icon>
        
        <!-- Logo aligned to the left -->
        <router-link to="/" class="d-flex align-center text-decoration-none ml-2">
          <img
            :src="logoUrl"
            alt="Banjino's Trading Post"
            style="height: 40px; max-width: 180px; object-fit: contain; cursor: pointer;"
          />
        </router-link>
        
        <v-spacer></v-spacer>
        
        <!-- Credit Status (only for B2B customers) -->
        <v-chip 
          v-if="authStore.isB2BCustomer && authStore.creditInfo"
          :color="authStore.creditInfo.status === 'approved' ? 'success' : 'warning'"
          size="small"
          class="mr-3"
        >
          Credit: ${{ formatCurrency(authStore.creditInfo.available) }}
        </v-chip>
        
        <!-- Shopping Cart (show for all authenticated users except read-only roles) -->
        <v-tooltip
          v-if="authStore.isAuthenticated"
          location="bottom"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              variant="text"
              @click="goToCart"
              class="mr-2 app-bar-btn-primary"
            >
              <v-badge
                :content="cartCount"
                :value="cartCount > 0"
                color="error"
                overlap
              >
                <v-icon>mdi-cart</v-icon>
              </v-badge>
            </v-btn>
          </template>
          <span>{{ getCartTooltip() }}</span>
        </v-tooltip>
        
        <!-- User Menu -->
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              class="mr-3 app-bar-btn-primary"
              aria-label="Account menu"
            >
              <v-icon start>mdi-account-circle</v-icon>
              <span v-if="!isMobile">{{ authStore.userDisplayName }}</span>
              <v-icon end>mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <!-- User Type Badge -->
            <v-list-item>
              <template v-slot:prepend>
                <v-icon :color="getUserTypeColor()">{{ getUserTypeIcon() }}</v-icon>
              </template>
              <v-list-item-title class="text-caption text-uppercase">
                {{ getUserTypeLabel() }}
              </v-list-item-title>
              <template v-slot:append>
                <v-chip
                  :color="getUserTypeColor()"
                  size="x-small"
                  variant="tonal"
                >
                  {{ getUserRoleDisplay() }}
                </v-chip>
              </template>
            </v-list-item>
            <v-divider class="my-1"></v-divider>
            <v-list-item
              prepend-icon="mdi-account"
              title="Profile"
              @click="viewProfile"
            ></v-list-item>
            <v-divider></v-divider>
            <v-list-item
              prepend-icon="mdi-logout"
              title="Logout"
              @click="logout"
            ></v-list-item>
          </v-list>
        </v-menu>
      </v-app-bar>

      <!-- Side Navigation Drawer -->
      <v-navigation-drawer
        v-model="drawer"
        app
        :rail="railState"
        class="navigation-drawer-offset"
        permanent
        @update:rail="railState = $event"
      >
        <v-list density="compact" nav style="padding-top: 10px; padding-bottom: 0;">
          <!-- Dynamic Menu Sections -->
          <template v-for="(section, sectionIndex) in menuSections" :key="`section-${sectionIndex}`">
            <!-- Section Header/Divider -->
            <template v-if="section.header">
              <v-divider v-if="sectionIndex > 0" class="my-3" />
              <v-list-subheader class="nav-subheader">
                <span v-if="!railState">{{ section.header }}</span>
                <v-icon v-else size="small" :color="getSectionColor(section.header)">
                  {{ getSectionIcon(section.header) }}
                </v-icon>
              </v-list-subheader>
            </template>
            
            <!-- Section Items -->
            <template v-for="item in section.items" :key="`${section.header}-${item.title}`">
              <v-tooltip
                location="end"
                :open-delay="200"
                :disabled="!railState"
              >
                <template v-slot:activator="{ props }">
                  <v-list-item
                    v-bind="props"
                    :to="item.to"
                    :prepend-icon="item.icon"
                    :title="item.title"
                    color="primary"
                    :value="item.to"
                    :class="[railState ? 'nav-item-collapsed' : 'nav-item-expanded', 'nav-item-with-tooltip']"
                  >
                    <!-- Badge support -->
                    <template v-slot:append v-if="item.badge && !railState">
                      <v-badge
                        v-if="item.badge.content === 'dynamic'"
                        :content="getCartCount()"
                        :color="item.badge.color"
                        inline
                      />
                      <v-badge
                        v-else
                        :content="item.badge.content"
                        :color="item.badge.color"
                        inline
                      />
                    </template>
                  </v-list-item>
                </template>
                <span>{{ item.title }}</span>
              </v-tooltip>
            </template>
          </template>
        </v-list>
      </v-navigation-drawer>
    </template>

    <!-- Main Content -->
    <v-main>
      <v-container fluid>
        <router-view />
      </v-container>
    </v-main>

    <!-- Global Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top"
      multi-line
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Brand Footer (hide on login page) -->
    <footer v-if="authStore.isAuthenticated" class="b2b-footer">
      <div class="footer-content">
        <p>© {{ new Date().getFullYear() }} BANJINO'S, LLC</p>
        <p>B2B WHOLESALE TRADE PORTAL</p>
      </div>
    </footer>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useSnackbar } from '@/composables/useSnackbar'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { getMenuForUser, filterMenuItems } from '@/config/menuStructure'
import logoUrl from './assets/images/banjinos-logo-black.png'

const router = useRouter()
const { mobile } = useDisplay()
const { snackbar } = useSnackbar()
const authStore = useAuthStore()
const cartStore = useCartStore()

// State
const drawer = ref(true)
const railState = ref(true) // Track if drawer is in rail (minimized) mode
const menuItems = ref([])

// Computed
const isMobile = computed(() => mobile.value)

// Get filtered menu items based on user type and permissions
const filteredMenuItems = computed(() => {
  if (!authStore.user) return []
  
  const baseMenu = getMenuForUser(authStore.user)
  const filtered = filterMenuItems(baseMenu, authStore.user, authStore.permissions)
  
  return filtered
})

// Group menu items by section for rendering
const menuSections = computed(() => {
  const sections = []
  let currentSection = null
  
  filteredMenuItems.value.forEach(item => {
    if (item.divider && item.header) {
      // Start a new section
      if (currentSection) {
        sections.push(currentSection)
      }
      currentSection = {
        header: item.header,
        items: []
      }
    } else if (!item.divider && currentSection) {
      // Add item to current section
      currentSection.items.push(item)
    } else if (!item.divider && !currentSection) {
      // Create default section if no header exists
      if (sections.length === 0 || sections[sections.length - 1].header) {
        sections.push({ header: null, items: [] })
      }
      sections[sections.length - 1].items.push(item)
    }
  })
  
  // Push the last section
  if (currentSection) {
    sections.push(currentSection)
  }
  
  // Filter out sections with no items
  return sections.filter(section => section.items.length > 0)
})

// Watch for user changes to update menu
watch(() => authStore.user, () => {
  menuItems.value = filteredMenuItems.value
}, { immediate: true })

// Methods
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

const getUserRoleDisplay = () => {
  const role = authStore.user?.role
  const roleMap = {
    'super_admin': 'Super Admin',
    'admin': 'Administrator',
    'manager': 'Manager',
    'employee': 'Employee',
    'sales_rep': 'Sales Rep',
    'customer': 'Customer',
    'owner': 'Owner'
  }
  return roleMap[role] || 'User'
}

const getUserTypeLabel = () => {
  const clientType = authStore.user?.clientType
  const role = authStore.user?.role
  
  if (clientType === 'B2B' && role === 'customer') {
    return 'B2B Partner'
  } else if (clientType === 'internal' || role === 'admin' || role === 'super_admin') {
    return 'Banjino\'s Team'
  } else if (clientType === 'dtc') {
    return 'Internal Operations'
  }
  return 'Wholesale Portal'
}

const getUserTypeIcon = () => {
  const clientType = authStore.user?.clientType
  const role = authStore.user?.role
  
  if (clientType === 'B2B' && role === 'customer') {
    return 'mdi-handshake'
  } else if (role === 'admin' || role === 'super_admin') {
    return 'mdi-shield-crown'
  } else if (clientType === 'internal' || clientType === 'dtc') {
    return 'mdi-badge-account'
  }
  return 'mdi-account-tie'
}

const getUserTypeColor = () => {
  const clientType = authStore.user?.clientType
  const role = authStore.user?.role
  
  // Using Banjino brand colors
  if (clientType === 'B2B' && role === 'customer') {
    return 'primary' // Orange - for B2B partners
  } else if (role === 'admin' || role === 'super_admin') {
    return 'secondary' // Green - for admins
  } else if (clientType === 'internal' || clientType === 'dtc') {
    return 'accent' // Tan - for internal team
  }
  return 'info' // Olive - fallback
}

const viewProfile = () => {
  router.push('/profile')
}

const logout = async () => {
  await authStore.logout()
  router.push('/login')
}

// Toggle drawer method
const toggleDrawer = () => {
  if (!drawer.value) {
    // If drawer is closed, open it
    drawer.value = true
    railState.value = false
  } else if (railState.value) {
    // If drawer is open but minimized (rail mode), expand it
    railState.value = false
  } else {
    // If drawer is open and expanded, minimize it
    railState.value = true
  }
}

// Get section icon based on header text
const getSectionIcon = (header) => {
  const iconMap = {
    'CLIENT OPERATIONS': 'mdi-domain',
    'ORDERING': 'mdi-cart-variant',
    'MY ACCOUNT': 'mdi-book-open-page-variant',
    'YOUR LEDGER': 'mdi-book-open-page-variant',
    'FINANCIAL': 'mdi-bank',
    'SALES TOOLS': 'mdi-cash-register',
    'ADMINISTRATION': 'mdi-briefcase-variant',
    'BACK OFFICE': 'mdi-briefcase-variant',
    'COMPANY': 'mdi-domain',
    'SUPPORT': 'mdi-help-circle'
  }
  return iconMap[header] || 'mdi-folder'
}

// Get section color based on header text
const getSectionColor = (header) => {
  const colorMap = {
    'CLIENT OPERATIONS': 'primary',
    'ORDERING': 'primary',
    'MY ACCOUNT': 'secondary',
    'YOUR LEDGER': 'secondary',
    'FINANCIAL': 'accent',
    'SALES TOOLS': 'info',
    'ADMINISTRATION': 'accent',
    'BACK OFFICE': 'accent',
    'COMPANY': 'secondary',
    'SUPPORT': 'info'
  }
  return colorMap[header] || 'primary'
}

const getCartCount = () => cartStore.itemCount
const cartCount = computed(() => cartStore.itemCount)

// Navigate to cart
const goToCart = () => {
  router.push('/cart')
}

// Get cart tooltip based on user type
const getCartTooltip = () => {
  if (authStore.user?.clientType === 'B2B' && authStore.user?.role === 'customer') {
    return 'Shopping Cart'
  } else if (authStore.user?.clientType === 'dtc' || authStore.user?.clientType === 'internal') {
    return 'Client Order Cart'
  }
  return 'Order Cart'
}

// Initialize
onMounted(async () => {
  // Initialize authentication from localStorage
  console.log('B2B App.vue: Initializing auth...')
  await authStore.initializeAuth()
  console.log('B2B App.vue: Auth initialized', {
    isAuthenticated: authStore.isAuthenticated,
    user: authStore.user,
    isB2BCustomer: authStore.isB2BCustomer,
    canAccessSalesOrderEntry: authStore.canAccessSalesOrderEntry,
    clientType: authStore.user?.clientType,
    role: authStore.user?.role
  })
})
</script>

<style>
/* Import global brand variables */
/* Brand styles imported via SCSS */

/* Global Vuetify overrides for B2B */
/* B2B uses the shared font system from SCSS */
/* Primary font is applied via shared styles */

/* Custom class for app bar buttons to use unified black color */
.app-bar-btn-primary,
.app-bar-btn-primary .v-btn__content,
.app-bar-btn-primary .v-icon {
  color: rgba(0, 0, 0, 0.87) !important;
}

.v-btn {
  text-transform: none !important;
}

/* Navigation drawer offset from app bar */
.navigation-drawer-offset {
  top: 60px !important;
  height: calc(100% - 60px) !important;
}

/* Make drawer expandable/collapsible */
.v-navigation-drawer--rail {
  width: 56px !important;
}

.v-navigation-drawer--rail .v-list-item__prepend {
  margin-inline-end: 0 !important;
}
/* Hide title text in rail mode */
.v-navigation-drawer--rail .v-list-item-title {
  display: none !important;
}

/* Tooltip animations and styling */
.v-tooltip > .v-overlay__content {
  background: rgba(33, 33, 33, 0.95) !important;
  color: white !important;
  padding: 8px 16px !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  animation: tooltipFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Tooltip arrow styling */
.v-tooltip .v-overlay__content::before {
  content: "";
  position: absolute;
  top: 50%;
  left: -6px;
  transform: translateY(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: rgba(33, 33, 33, 0.95);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Navigation item styling to match admin */
.nav-item-collapsed {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.v-navigation-drawer--rail .v-list-item {
  padding-left: 8px !important;
  padding-right: 13px !important;
}

.nav-item-expanded {
  padding-left: 16px !important;
}

.nav-item-with-tooltip .v-list-item__prepend {
  width: auto;
}

/* Navigation subheaders */
.nav-subheader {
  font-family: var(--font-ui);  /* UI elements keep clean font */
  font-size: 0.75rem;
  font-weight: var(--weight-semibold);
  color: var(--banjino-green);
  text-transform: uppercase;
  letter-spacing: var(--spacing-medium);
  padding: 16px 16px 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.v-list--density-compact .v-list-subheader {
  min-height: 32px;
  padding-inline-start: calc(12px + var(--indent-padding)) !important;
}

/* Center subheader icons when rail */
.v-navigation-drawer--rail .nav-subheader {
  justify-content: center;
  padding: 16px 0 8px;
  text-align: center;
}

.v-navigation-drawer--rail .nav-subheader > * {
  width: 100%;
  display: flex;
  justify-content: center;
}


/* Navigation item hover effects */
.nav-item-with-tooltip {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative;
  overflow: hidden;
}

.nav-item-with-tooltip:hover {
  transform: translateX(2px);
}

.nav-item-with-tooltip:hover .v-list-item__prepend .v-icon {
  transform: scale(1.1);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Ripple effect on hover */
.nav-item-with-tooltip::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(var(--v-theme-primary), 0.1);
  transform: translate(-50%, -50%);
  transition: width 0.3s, height 0.3s;
}

.nav-item-with-tooltip:hover::after {
  width: 100%;
  height: 100%;
}

/* Enhanced shadow on hover */
.nav-item-with-tooltip:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Tooltip fade-in animation */
@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Footer */
.b2b-footer {
  background-color: var(--color-secondary);
  color: var(--color-accent);
  text-align: center;
  padding: var(--space-lg);
  font-size: var(--text-small);
  letter-spacing: var(--spacing-medium);
  margin-top: auto;
}

.footer-content p {
  margin: 2px 0;
  font-family: var(--font-ui);  /* UI elements keep clean font */
}

.footer-content p:first-child {
  font-weight: var(--weight-semibold);
}

/* Responsive */
@media (max-width: 960px) {
  .header-text .business-title {
    font-size: 1rem;
  }
  
  .header-text .business-subtitle {
    font-size: 0.7rem;
  }
  
  .credit-display {
    display: none !important;
  }
}
</style>
